import {
  sortPropertiesByAddress,
  getPropertyBoligmappaNumber,
  isPropertyOwned,
} from '../../utils/properties';

import { areIdsEqual } from '../../utils/strings';

const defaultState = {
  owned: null,
  fetching: true,
  fetchingError: null,
  activeProperty: null,
  sharedPrivately: null,
  sharedPublicly: null,
  documentsToSigned: null,
  fetchingDocumentsToSigned: false,
  dialogToSignDocSeen: {},
  adCarouselClosed: false,
  propertyValueIncreases: null,
  propertyValueEstimates: null,
  propertyDocumentStatistics: null,
  propertyCompanyStats: null,
};

export const ADD_PROPERTY = 'ADD_PROPERTY';
export const SET_PROPERTIES = 'SET_PROPERTIES';
export const SET_PUBLIC_PROPERTY = 'SET_PUBLIC_PROPERTY';
export const UPDATE_PROPERTY = 'UPDATE_PROPERTY';
export const SET_ACTIVE_PROPERTY = 'SET_ACTIVE_PROPERTY';
export const SET_FETCHING_PROPERTIES = 'SET_FETCHING_PROPERTIES';
export const SET_FETCHING_PROPERTIES_ERROR = 'SET_FETCHING_PROPERTIES_ERROR';
export const SET_DOCUMENTS_TO_SIGNED = 'SET_DOCUMENTS_TO_SIGNED';
export const SET_FETCHING_DOCUMENTS_TO_SIGNED =
  'SET_FETCHING_DOCUMENTS_TO_SIGNED';
export const SET_DIALOG_TO_SIGN_DOC_SEEN = 'SET_DIALOG_TO_SIGN_DOC_SEEN';
export const SET_AD_CAROUSEL_CLOSED = 'SET_AD_CAROUSEL_CLOSED';
export const SET_PROPERTY_VALUE_INCREASES = 'SET_PROPERTY_VALUE_INCREASES';
export const SET_PROPERTY_VALUE_ESTIMATES = 'SET_PROPERTY_VALUE_ESTIMATES';
export const SET_PROPERTY_DOCUMENT_STATISTICS =
  'SET_PROPERTY_DOCUMENT_STATISTICS';
export const SET_PROPERTY_COMPANY_STATS = 'SET_PROPERTY_COMPANY_STATS';

export const getUpdatedProperty = (storedProperty, updatedProperty) =>
  areIdsEqual(
    getPropertyBoligmappaNumber(storedProperty),
    getPropertyBoligmappaNumber(updatedProperty)
  )
    ? updatedProperty
    : storedProperty;

export default (state = defaultState, action) => {
  switch (action.type) {
    case ADD_PROPERTY:
      const { isOwned, property } = action.payload;

      return {
        ...state,
        owned: [...state.owned, isOwned ? property : null],
        sharedPrivately: [...state.sharedPrivately, isOwned ? null : property],
      };
    case SET_PROPERTIES:
      return {
        ...state,
        owned: (action.payload.owned || [])
          .map((data) => ({ ...data, owned: true }))
          .sort(sortPropertiesByAddress),
        sharedPrivately: (action.payload.sharedPrivately || [])
          .map((data) => ({ ...data, owned: false }))
          .sort(sortPropertiesByAddress),
      };
    case SET_PUBLIC_PROPERTY:
      return action.payload; // new
    case UPDATE_PROPERTY:
      const updatedProperty = action.payload;

      return {
        ...state,
        sharedPrivately: state.sharedPrivately.map((storedProperty) =>
          getUpdatedProperty(storedProperty, updatedProperty)
        ),
        owned: state.owned.map((storedProperty) =>
          getUpdatedProperty(storedProperty, updatedProperty)
        ),
        activeProperty: getUpdatedProperty(
          state.activeProperty,
          updatedProperty
        ),
      };
    case SET_ACTIVE_PROPERTY:
      return {
        ...state,
        activeProperty: {
          ...action.payload,
          owned: isPropertyOwned(action.payload),
        },
      };
    case SET_FETCHING_PROPERTIES:
      return {
        ...state,
        fetching: action.payload,
      };
    case SET_FETCHING_PROPERTIES_ERROR:
      return {
        ...state,
        fetchingError: action.payload,
      };
    case SET_DOCUMENTS_TO_SIGNED:
      return {
        ...state,
        documentsToSigned: action.payload,
      };
    case SET_FETCHING_DOCUMENTS_TO_SIGNED:
      return {
        ...state,
        fetchingDocumentsToSigned: action.payload,
      };
    case SET_DIALOG_TO_SIGN_DOC_SEEN:
      return {
        ...state,
        dialogToSignDocSeen: action.payload,
      };
    case SET_AD_CAROUSEL_CLOSED:
      return {
        ...state,
        adCarouselClosed: action.payload,
      };
    case SET_PROPERTY_VALUE_INCREASES:
      return {
        ...state,
        propertyValueIncreases: action.payload,
      };
    case SET_PROPERTY_VALUE_ESTIMATES:
      return {
        ...state,
        propertyValueEstimates: action.payload,
      };
    case SET_PROPERTY_DOCUMENT_STATISTICS:
      return {
        ...state,
        propertyDocumentStatistics: action.payload,
      };
    case SET_PROPERTY_COMPANY_STATS:
      return {
        ...state,
        propertyCompanyStats: action.payload,
      };
    default:
      return state;
  }
};

export const sGetProperties = (state) => state.properties;
export const sGetOwnedProperties = (state) => sGetProperties(state).owned;

export const sGetSharedPrivatelyProperties = (state) =>
  sGetProperties(state).sharedPrivately;

export const sGetPublicProperty = (state) => state.properties.activeProperty; // new

export const sGetFetchingProperties = (state) => sGetProperties(state).fetching;

export const sGetActiveProperty = (state) =>
  sGetProperties(state).activeProperty;

export const sGetFetchingPropertiesError = (state) =>
  sGetProperties(state).fetchingError;

export const sGetPropertiesLoaded = (state) =>
  !!(sGetOwnedProperties(state) || !!sGetSharedPrivatelyProperties(state));

export const sGetAllProperties = (state) => [
  ...(sGetOwnedProperties(state) || []),
  ...(sGetSharedPrivatelyProperties(state) || []),
];

export const sGetPropertyByBoligmappaNumber = (state, boligmappaNumber) =>
  sGetAllProperties(state).find(
    (property) => getPropertyBoligmappaNumber(property) === boligmappaNumber
  );

export const sGetDocumentsToSigned = (state) =>
  sGetProperties(state).documentsToSigned;

export const sGetFetchingDocumentsToSigned = (state) =>
  sGetProperties(state).fetchingDocumentsToSigned;

export const sGetDialogToSignDocSeen = (state) =>
  sGetProperties(state).dialogToSignDocSeen;

export const sGetAdCarouselClosed = (state) =>
  sGetProperties(state).adCarouselClosed;

export const sGetPropertyValueIncreases = (state) =>
  sGetProperties(state).propertyValueIncreases;

export const sGetPropertyValueEstimates = (state) =>
  sGetProperties(state).propertyValueEstimates;

export const sGetPropertyDocumentStatistics = (state) =>
  sGetProperties(state).propertyDocumentStatistics;

export const sGetPropertyCompanyStats = (state) =>
  sGetProperties(state).propertyCompanyStats;
